/**
 * Derives which design and mapping IDs should be treated as read-only while generation
 * is in progress, using the current rule graph (React Flow elements) and websocket status.
 * Server may use a different snapshot; this is a best-effort UI guard.
 */
import { isProjectRunning } from '../../Project/StatusBlock/StatusBlock';

/** @param {Record<string, { status?: string }>|null|undefined} outputsMap */
export function getBusyOutputIds(outputsMap) {
  const set = new Set();
  if (!outputsMap || typeof outputsMap !== 'object') return set;
  for (const [key, val] of Object.entries(outputsMap)) {
    if (val && isProjectRunning(val.status)) {
      set.add(Number(key));
    }
  }
  return set;
}

/**
 * When at least one output is mid-generation, rely on per-output path locks only.
 * Otherwise, project-level "running" (e.g. parsing) still locks the whole rule UI.
 * @param {{ status?: string, outputs?: Record<string, { status?: string }> }|null|undefined} socketProject
 */
export function shouldUseProjectWideUiLock(socketProject) {
  if (!socketProject) return false;
  const busyOutputs = getBusyOutputIds(socketProject.outputs);
  if (busyOutputs.size > 0) return false;
  return isProjectRunning(socketProject.status);
}

/** @param {object|undefined} data graphic node data */
export function collectDesignIdsFromGraphicData(data) {
  const ids = new Set();
  const selected = data?.selected;
  if (Array.isArray(selected)) {
    selected.forEach((id) => {
      if (id !== '' && id != null && !Number.isNaN(Number(id))) ids.add(Number(id));
    });
  }
  const promo = data?.promoTemplates;
  if (promo && typeof promo === 'object') {
    for (const v of Object.values(promo)) {
      const pt = v?.promo_template;
      if (pt !== '' && pt != null && !Number.isNaN(Number(pt))) ids.add(Number(pt));
    }
  }
  return ids;
}

/**
 * Reverse adjacency: target -> sources (predecessors).
 * @param {Array<{ id?: string, source?: string, target?: string, type?: string }>} elements
 */
export function buildReverseAdjacency(elements) {
  const rev = new Map();
  if (!Array.isArray(elements)) return rev;
  for (const el of elements) {
    if (!el.source || !el.target) continue;
    if (el.type && el.type !== 'default') continue;
    const list = rev.get(el.target) || [];
    list.push(el.source);
    rev.set(el.target, list);
  }
  return rev;
}

/** @param {string} startId export node id */
export function collectUpstreamNodeIds(startId, reverseAdj) {
  const visited = new Set();
  const queue = [startId];
  visited.add(startId);
  while (queue.length) {
    const n = queue.shift();
    const preds = reverseAdj.get(n) || [];
    for (const p of preds) {
      if (!visited.has(p)) {
        visited.add(p);
        queue.push(p);
      }
    }
  }
  return visited;
}

/**
 * Export node ids whose backend output is in the busy set.
 * @param {Array} elements
 * @param {{ outputs?: Array<{ id: number|string, name: string }> }} project
 * @param {Set<number>} busyOutputIds
 */
export function getExportNodeIdsForBusyOutputs(elements, project, busyOutputIds) {
  const ids = [];
  if (!Array.isArray(elements) || !busyOutputIds.size) return ids;
  const outputs = project?.outputs || [];
  for (const el of elements) {
    if (el.type !== 'export' || !el.data?.name) continue;
    const output = outputs.find((o) => o.name === el.data.name);
    if (output && busyOutputIds.has(Number(output.id))) {
      ids.push(el.id);
    }
  }
  return ids;
}

/**
 * All design ids referenced by any graphic node in elements.
 * @param {Array} elements
 */
export function collectAllGraphicDesignIds(elements) {
  const out = new Set();
  if (!Array.isArray(elements)) return out;
  for (const el of elements) {
    if (el.type === 'graphic' && el.data) {
      collectDesignIdsFromGraphicData(el.data).forEach((id) => out.add(id));
    }
  }
  return out;
}

/**
 * @param {{ elements?: Array, project?: object|null, socketProject?: { status?: string, outputs?: Record<string, { status?: string }> }|null }} args
 * @returns {Set<number>}
 */
export function getLockedDesignIds({ elements, project, socketProject }) {
  const empty = new Set();
  if (!Array.isArray(elements) || !project) return empty;

  const busyOutputIds = getBusyOutputIds(socketProject?.outputs);

  // Per-output generation often mirrors project status as "running"; prefer path lock only.
  if (busyOutputIds.size > 0) {
    const exportNodeIds = getExportNodeIdsForBusyOutputs(elements, project, busyOutputIds);
    if (!exportNodeIds.length) return empty;

    const rev = buildReverseAdjacency(elements);
    const locked = new Set();
    const nodeById = new Map(elements.filter((e) => e.id).map((e) => [e.id, e]));

    for (const expId of exportNodeIds) {
      const upstream = collectUpstreamNodeIds(expId, rev);
      for (const nodeId of upstream) {
        const node = nodeById.get(nodeId);
        if (node && node.type === 'graphic' && node.data) {
          collectDesignIdsFromGraphicData(node.data).forEach((id) => locked.add(id));
        }
      }
    }
    return locked;
  }

  if (isProjectRunning(socketProject?.status)) {
    return collectAllGraphicDesignIds(elements);
  }

  return empty;
}

/**
 * Mapping (feed template) ids used on export nodes that are currently busy.
 * @param {{ elements?: Array, project?: object|null, socketProject?: object|null }} args
 * @returns {Set<number>}
 */
export function getLockedMappingIds({ elements, project, socketProject }) {
  const result = new Set();
  if (!Array.isArray(elements) || !project) return result;

  const busyOutputIds = getBusyOutputIds(socketProject?.outputs);

  if (busyOutputIds.size > 0) {
    const outputs = project.outputs || [];
    for (const el of elements) {
      if (el.type !== 'export' || !el.data?.name) continue;
      const output = outputs.find((o) => o.name === el.data.name);
      if (!output || !busyOutputIds.has(Number(output.id))) continue;
      const num = Number(el.data.output_type);
      if (num > 0) result.add(num);
    }
    return result;
  }

  if (isProjectRunning(socketProject?.status)) {
    for (const el of elements) {
      if (el.type !== 'export' || !el.data) continue;
      const num = Number(el.data.output_type);
      if (num > 0) result.add(num);
    }
  }
  return result;
}
